import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FeelingGoal } from '../../@types/seen-apps';

import styles from './GoalForm.module.scss';
import { getWordTranslated } from '../../tools/feelingTools';
import GlobalStateContext from '../../context/globalState/GlobalStateContext';
import Card from '../Card/Card';
import GoalHitBadge, { GoalHitProps } from './GoalHitBadge';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { GoalIcon } from '../RemixIcons';
import GoalFormModal from './GoalFormModal';

type Report = Record<string, GoalHitProps[]>;

type GoalResumeProps = {
  feelingWordId: string;
  values: FeelingGoal[];
  disabled?: boolean;
  onChange: (value: FeelingGoal) => void;
  onDelete: (goal: FeelingGoal) => void;
};

const getKey = (level: number) => {
  return level === -2
    ? 'stronglyAway'
    : level === -1
    ? 'away'
    : level === 1
    ? 'closer'
    : level === 2
    ? 'stronglyCloser'
    : 'none';
};

const GoalResume = ({
  reportKey,
  items,
}: {
  reportKey: string;
  items: GoalHitProps[] | undefined;
}) => {
  const { t } = useTranslation('i18n');

  return !items?.length ? (
    <></>
  ) : (
    <div className={styles.goalResumeContainer}>
      <div className={styles.goalResumeTitle}>
        {t(
          `pages.feeling.goal.resume.${reportKey}_${
            items.length > 1 ? 'many' : 'one'
          }`
        )}
      </div>
      {items.map((goal) => (
        <GoalHitBadge {...goal} />
      ))}
    </div>
  );
};

const GoalsResume = ({
  values,
  feelingWordId,
  disabled,
  onChange,
  onDelete,
}: GoalResumeProps) => {
  const { t } = useTranslation('i18n');
  const { state } = useContext(GlobalStateContext);

  const [report, setReport] = useState<Report>({});
  const [feeling] = useState<string>(getWordTranslated(state, feelingWordId));
  const [goalToUpdate, setGoalToUpdate] = useState<FeelingGoal>();

  useEffect(() => {
    const report: Report = {};

    values.forEach((goal) => {
      const key = getKey(goal.level);
      const subGoals = goal.subs.filter((s) => s.level);
      const reportRow = report[key] || [];
      if (goal.level || !subGoals.length) {
        reportRow.push({
          goalId: goal.id,
          ranking: goal.ranking,
          subGoalId: undefined,
          level: goal.level,
        });
      }
      report[key] = reportRow;
      subGoals.forEach((subGoal) => {
        if (!subGoal.level) {
          return;
        }
        const key = getKey(subGoal.level);
        const reportRow = report[key] || [];

        reportRow.push({
          goalId: goal.id,
          subGoalId: subGoal.id,
          level: subGoal.level,
          ranking: goal.ranking,
        });
        report[key] = reportRow;
      });
    });
    setReport(report);
  }, [feelingWordId, state, t, values]);

  const handleUpdateGoal = (goal: FeelingGoal) => {
    setGoalToUpdate(goal);
  };

  const handleUpdateGoalClosed = () => {
    setGoalToUpdate(undefined);
  };

  const handleGoalUpdated = (goal: FeelingGoal) => {
    setGoalToUpdate(undefined);
    onChange(goal);
  };
  const handleGoalDeleted = () => {
    if (goalToUpdate) {
      onDelete(goalToUpdate);
      setGoalToUpdate(undefined);
    }
  };

  return (
    <div className={styles.resume}>
      {!Object.keys(report).length && disabled ? (
        t('pages.feeling.goal.noValue')
      ) : !Object.keys(report).length ? (
        <></>
      ) : (
        <Card
          title={t(`pages.feeling.goal.resume.description`, {
            word: feeling,
          })}
          actionIcon={
            disabled ? (
              <></>
            ) : (
              <DropdownButton
                title={'...'}
                className={styles.inputButton}
                variant='outline-primary'
                id='input-group-dropdown-1'
              >
                {values.map((goal) => (
                  <Dropdown.Item
                    key={`update-goal-${goal}`}
                    onClick={() => {
                      handleUpdateGoal(goal);
                    }}
                  >
                    <GoalIcon /> {t(`report.goalForm.axes.${goal.id}.label`)}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            )
          }
        >
          <div className={styles.resumeContent}>
            {['stronglyAway', 'stronglyCloser', 'away', 'closer', 'none'].map(
              (key) => (
                <GoalResume
                  key={`goal-report-${key}`}
                  reportKey={key}
                  items={report[key]}
                />
              )
            )}
          </div>
        </Card>
      )}
      {goalToUpdate && (
        <GoalFormModal
          show
          onClose={handleUpdateGoalClosed}
          value={goalToUpdate}
          onChange={handleGoalUpdated}
          onDelete={handleGoalDeleted}
        />
      )}
    </div>
  );
};

export default GoalsResume;
